import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'aws-amplify/auth';
import { useAlerts, useExpedientes, useMe } from '../lib/hooks';
import { DEMO } from '../lib/demo';
import { DEV_AUTH } from '../lib/api';
import { useDemo } from '../lib/demo-tour';
import { ProgressBar, friendlyEstado } from './ui';
import { Assistant, useAssistantChat } from './assistant';
import { AiSpark } from './assistant/tool-cards/spark';
import {
  ArrowLeft,
  Menu,
  Search,
  Bell,
  Settings,
  ChevronRight,
  FileText,
  Building2,
  SquarePen,
} from 'lucide-react';

// Console/compliance route prefixes. There is a single app shell now (no world
// toggle); this is used only to route global-search results to the right detail
// lens — the analyst view (/revision/:id) from a compliance screen, the friendly
// view (/expedientes/:id) from the Registro screens.
const CONSOLE_PREFIXES = ['/agente', '/panel', '/revision', '/alertas', '/documentos', '/alcance', '/validacion-masiva', '/vigilancia-registral', '/matriz-riesgo', '/auditoria', '/papelera', '/usuarios', '/admin'];
const isConsole = (path: string) => CONSOLE_PREFIXES.some((p) => path.startsWith(p));

const initials = (name: string) =>
  name.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

type MenuKey = 'notif' | 'settings' | 'profile';

export function AppHeader({
  onBack,
  onMenu,
  progress,
  showBrand = true,
  showActions = true,
}: {
  onBack?: (() => void) | undefined;
  onMenu?: (() => void) | undefined;
  progress?: number | undefined;
  showBrand?: boolean;
  showActions?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: exps } = useExpedientes();
  const { data: alerts } = useAlerts();

  const [menu, setMenu] = useState<MenuKey | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const demo = useDemo();
  const { newConversation } = useAssistantChat();

  // Start a fresh assistant conversation and land on the workspace — reachable
  // from anywhere via the top-bar button or ⌘⇧O, so you never scroll a rail.
  function startNewConversation() {
    newConversation();
    navigate('/agente');
  }

  // Tour "live action": open the assistant (and auto-ask) when the tour requests it.
  const tourOpensAssistant = demo.playing && demo.action === 'openAssistant';
  useEffect(() => {
    if (demo.playing) setAiOpen(demo.action === 'openAssistant');
  }, [demo.playing, demo.action]);

  // Pages open the drawer via a DOM event (see assistant/page-actions.ts
  // `openAssistant`) — same surface the ⌘K shortcut toggles.
  useEffect(() => {
    const onOpen = () => setAiOpen(true);
    window.addEventListener('cumpli:open-assistant', onOpen);
    return () => window.removeEventListener('cumpli:open-assistant', onOpen);
  }, []);

  // Global ⌘K / Ctrl+K launcher — toggles the Asistente IA slide-over from
  // anywhere (both worlds share this header). Standard command-palette behavior:
  // always toggle, and pre-empt the browser default. Cleaned up on unmount.
  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      const mod = (e.metaKey || e.ctrlKey) && !e.altKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setAiOpen((v) => !v);
      } else if (mod && e.shiftKey && (e.key === 'o' || e.key === 'O')) {
        // ⌘⇧O / Ctrl+Shift+O — new conversation (the ChatGPT/Claude convention).
        e.preventDefault();
        startNewConversation();
      }
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, []);

  const expiring = useMemo(
    () => (exps ?? []).flatMap((e) => e.docs.filter((d) => d.estado === 'Por Vencer' || d.estado === 'Vencido').map((d) => ({ e, d }))),
    [exps]
  );
  const notifCount = (alerts?.length ?? 0) + expiring.length;

  function logout() {
    setMenu(null);
    // Clear any persisted assistant transcripts — they can contain client names /
    // risk detail and must not linger on disk after sign-out (AML privacy).
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('cumpli:assistant:')) localStorage.removeItem(k);
      }
    } catch {
      /* storage unavailable — nothing to clear */
    }
    // Local dev-auth demo has no login portal and no Cognito session to end:
    // just return to the default screen instead of signing out to /login.
    if (DEV_AUTH) return navigate('/agente');
    if (DEMO) return navigate('/login');
    void signOut().then(() => navigate('/login'));
  }

  return (
    <>
    <header className="sticky top-0 z-40 border-b border-line/70 bg-canvas/85 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-3 px-4 sm:px-6">
        {/* left: menu (mobile) + back + brand */}
        <div className="flex items-center gap-2.5">
          {onMenu && (
            <button
              onClick={onMenu}
              aria-label="Abrir menú"
              className="grid h-10 w-10 place-items-center rounded-full text-ink-soft transition-colors hover:bg-panel hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={2} />
            </button>
          )}
          {onBack && (
            <button onClick={onBack} aria-label="Atrás" className="grid h-10 w-10 place-items-center rounded-full text-ink-soft transition-colors hover:bg-panel hover:text-ink">
              <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            </button>
          )}
          {showBrand && (
            <button onClick={() => navigate('/agente')} className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-primary-mid to-primary-dark font-serif text-base font-semibold text-white">
                C
              </span>
              <span className="hidden font-serif text-lg font-medium leading-none text-ink xs:inline">Cumpli</span>
            </button>
          )}
        </div>

        {/* right cluster */}
        {showActions && (
          <div className="ml-auto flex items-center gap-1">
            <div data-tour="search-palette-btn">
              <IconButton label="Buscar" onClick={() => setSearchOpen(true)}>
                <Search className="h-5 w-5" strokeWidth={1.75} />
              </IconButton>
            </div>

            {/* Always-visible "new conversation" — reachable from any page/scroll
                position (also ⌘⇧O), so you never scroll the conversations rail. */}
            <button
              onClick={startNewConversation}
              title="Nueva conversación (⌘⇧O)"
              aria-label="Nueva conversación"
              aria-keyshortcuts="Meta+Shift+O Control+Shift+O"
              className="grid h-11 w-11 place-items-center rounded-full text-ink-soft transition-colors hover:bg-panel hover:text-ink"
            >
              <SquarePen className="h-5 w-5" strokeWidth={1.75} />
            </button>

            <button
              data-tour="ai-assistant"
              onClick={() => setAiOpen(true)}
              title={t('assistant.shortcutHint')}
              aria-label={t('assistant.shortcutHint')}
              aria-keyshortcuts="Meta+K Control+K"
              className="ml-0.5 inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-line-strong bg-panel px-3 py-2 text-sm font-bold text-primary-dark transition-colors hover:border-primary"
            >
              <AiSpark className="h-4 w-4" />
              <span className="hidden md:inline">Cumpli AI</span>
              <kbd className="hidden rounded border border-line-strong bg-canvas px-1 font-mono text-[10px] font-semibold text-ink-faint lg:inline">
                ⌘K
              </kbd>
            </button>

            <div data-tour="notifications">
              <IconButton label="Notificaciones" onClick={() => setMenu(menu === 'notif' ? null : 'notif')}>
                <Bell className="h-5 w-5" strokeWidth={1.75} />
                {notifCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-white">
                    {notifCount > 99 ? '99+' : notifCount}
                  </span>
                )}
              </IconButton>
            </div>

            <IconButton label="Ajustes" onClick={() => setMenu(menu === 'settings' ? null : 'settings')}>
              <Settings className="h-5 w-5" strokeWidth={1.75} />
            </IconButton>

            <button
              onClick={() => setMenu(menu === 'profile' ? null : 'profile')}
              className="ml-0.5 grid h-11 w-11 place-items-center rounded-full bg-side text-sm font-bold text-primary-rose"
            >
              {me ? initials(me.fullName) : '·'}
            </button>
          </div>
        )}
      </div>

      {progress !== undefined && (
        <div className="mx-auto w-full max-w-6xl px-4 pb-3 sm:px-6">
          <ProgressBar value={progress} />
        </div>
      )}

      {/* dropdowns — direct children of the sticky header so they anchor to the
          viewport edge (right-4) and never overflow on narrow phones */}
      {menu === 'notif' && (
        <Dropdown title="Notificaciones">
          {(alerts ?? []).slice(0, 3).map((a) => (
            <DropItem key={a.id} onClick={() => { setMenu(null); navigate('/alertas'); }}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-tone-red-bg text-tone-red"><Bell className="h-4 w-4" strokeWidth={2} /></span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-ink">{a.tipo}</span>
                <span className="block truncate text-xs text-ink-faint">{a.cliente}</span>
              </span>
            </DropItem>
          ))}
          {expiring.slice(0, 2).map(({ e, d }, i) => (
            <DropItem key={i} onClick={() => { setMenu(null); navigate(`/revision/${e.id}`); }}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-tone-amber-bg text-tone-amber"><FileText className="h-4 w-4" strokeWidth={2} /></span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-ink">{d.name} · por vencer</span>
                <span className="block truncate text-xs text-ink-faint">{e.name}</span>
              </span>
            </DropItem>
          ))}
          {notifCount === 0 && <p className="px-4 py-6 text-center text-sm text-ink-faint">Sin notificaciones</p>}
        </Dropdown>
      )}

      {menu === 'settings' && (
        <Dropdown title="Ajustes">
          <div className="px-4 py-2 text-sm">
            <Row k="Entidad" v={me?.tenantName ?? '—'} />
            <Row k="Plan" v="Piloto" />
            <Row k="Idioma" v="es-PA" />
          </div>
          <div className="my-1 border-t border-line" />
          <DropItem onClick={() => { setMenu(null); navigate('/usuarios'); }}>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-canvas text-ink-soft"><Building2 className="h-4 w-4" strokeWidth={2} /></span>
            <span className="text-sm font-semibold text-ink">Usuarios y roles</span>
            <ChevronRight className="ml-auto h-4 w-4 text-ink-faint" strokeWidth={2} />
          </DropItem>
        </Dropdown>
      )}

      {menu === 'profile' && (
        <Dropdown title={me?.fullName ?? ''} subtitle={me?.email}>
          <DropItem onClick={logout}>
            <span className="text-sm font-semibold text-ink">Salir</span>
          </DropItem>
        </Dropdown>
      )}

    </header>

    {/* click-catcher for dropdowns (sibling of header so it never covers header controls) */}
    {menu && <div className="fixed inset-0 z-30" onClick={() => setMenu(null)} />}
    {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
    <Assistant open={aiOpen} onClose={() => setAiOpen(false)} autoAsk={tourOpensAssistant} />
    </>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="relative grid h-11 w-11 place-items-center rounded-full text-ink-soft transition-colors hover:bg-panel hover:text-ink"
    >
      {children}
    </button>
  );
}

function Dropdown({ title, subtitle, children }: { title: string; subtitle?: string | undefined; children: React.ReactNode }) {
  return (
    <div className="absolute right-4 top-full z-40 mt-1 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-line bg-panel shadow-pop" onClick={(e) => e.stopPropagation()}>
      <div className="border-b border-line px-4 py-3">
        <div className="truncate text-sm font-bold text-ink">{title}</div>
        {subtitle && <div className="truncate text-xs text-ink-faint">{subtitle}</div>}
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}

function DropItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-canvas">
      {children}
    </button>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-ink-faint">{k}</span>
      <span className="font-semibold text-ink">{v}</span>
    </div>
  );
}

function SearchPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: exps } = useExpedientes();
  // Route to the lens matching where the search was opened: the analyst detail
  // from a compliance screen, the friendly Registro detail otherwise.
  const detailBase = isConsole(pathname) ? '/revision' : '/expedientes';
  const [q, setQ] = useState('');
  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    const all = exps ?? [];
    if (!term) return all.slice(0, 6);
    return all.filter((e) => e.name.toLowerCase().includes(term) || e.docId.toLowerCase().includes(term)).slice(0, 8);
  }, [exps, q]);

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 top-24 mx-auto w-full max-w-xl px-4">
        <div className="overflow-hidden rounded-2xl border border-line bg-panel shadow-pop">
          <div className="flex items-center gap-3 border-b border-line px-4">
            <Search className="h-5 w-5 text-ink-faint" strokeWidth={2} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cliente por nombre o cédula…"
              className="h-14 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-faint"
            />
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {results.length === 0 && <p className="px-4 py-6 text-center text-sm text-ink-faint">Sin resultados</p>}
            {results.map((e) => {
              const fe = friendlyEstado(e.estado);
              return (
                <button
                  key={e.id}
                  onClick={() => { onClose(); navigate(`${detailBase}/${e.id}`); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-canvas"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-ink">{e.name}</span>
                    <span className="block truncate font-mono text-[11px] text-ink-faint">{e.docId}</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-canvas px-2.5 py-1 text-[11px] font-medium text-ink-soft">{fe.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
